import type {
  LifeRecord,
  EarthRow,
  EarthDivergence,
  LifeCareerStage,
  LifeFuturePath,
  LifeTimelineEntry,
  LifeBeat,
  EarthDimension,
} from "../../types";
import type { NarrativeContext, NarrativeResult } from "../../narrative";
import { ENGINE_VERSION } from "../../lifeChapters";
import { lifeStream, ageFor, clampLevel, pick, pickDistinct, listJoin } from "./lifeHelpers";
import { fill } from "../compose";
import {
  AGE_COUNTING_NOTES,
  STRANGE_BIOGRAPHY_OPENERS,
  STRANGE_BIOGRAPHY_MIDDLES,
  STRANGE_BIOGRAPHY_CLOSERS,
  STRANGE_WORKPLACES,
  STRANGE_WEALTH_BRACKETS,
  STRANGE_SOCIAL_CLASSES,
  STRANGE_DWELLINGS,
  STRANGE_ENVIRONMENTS,
  STRANGE_LIFESTYLES,
  STRANGE_POSSESSIONS_BANK,
  STRANGE_TRANSPORTS,
  STRANGE_FAMILY_DESCRIPTIONS,
  STRANGE_FRIENDSHIPS,
  STRANGE_SOCIAL_CIRCLES,
  STRANGE_CHILDHOODS,
  STRANGE_EDUCATIONS,
  STRANGE_SOCIETY_SUMMARIES,
  STRANGE_SOCIETY_LAWS,
  STRANGE_SOCIETY_CULTURES,
  STRANGE_SOCIETY_INSTITUTIONS,
  STRANGE_SOCIETY_EXPECTATIONS,
  STRANGE_SOCIETY_OPPORTUNITIES,
  STRANGE_WORLD_TECHNOLOGIES,
  STRANGE_WORLD_SCIENCES,
  STRANGE_WORLD_GEOGRAPHIES,
  STRANGE_WORLD_HISTORIES,
  STRANGE_WORLD_ENVIRONMENTS,
  EARTH_DIMENSION_ORDER,
} from "./strange.life.banks";

/**
 * Builds a rich, deterministic LifeRecord for the Strange Self of the Multiverse Life Dossier.
 *
 * All physical and cultural features are derived from the universe's model parameters:
 * - config.gravity, config.topology, config.dimensions, config.timeArrow
 * - profile.stage, profile.fate, profile.habitability
 * - ctx.condition ("hospitable" | "marginal" | "hostile")
 *
 * Randomness is strictly scoped to `lifeStream(ctx, "strange.<label>")`.
 */
export function buildStrangeLife(ctx: NarrativeContext, base: NarrativeResult): LifeRecord {
  const { universe, resolved, condition } = ctx;
  const { config, profile } = universe;

  // Stream generators with independent labels
  const ageRng = lifeStream(ctx, "strange.age");
  const bioRng = lifeStream(ctx, "strange.bio");
  const careerRng = lifeStream(ctx, "strange.career");
  const standingRng = lifeStream(ctx, "strange.standing");
  const homeRng = lifeStream(ctx, "strange.home");
  const peopleRng = lifeStream(ctx, "strange.people");
  const formativeRng = lifeStream(ctx, "strange.formative");
  const societyRng = lifeStream(ctx, "strange.society");
  const worldRng = lifeStream(ctx, "strange.world");
  const diffRng = lifeStream(ctx, "strange.diff");
  const dayRng = lifeStream(ctx, "strange.day");
  const futuresRng = lifeStream(ctx, "strange.futures");
  const timelineRng = lifeStream(ctx, "strange.timeline");
  const earthRng = lifeStream(ctx, "strange.earth");

  // Age calculation (19..84)
  const age = ageFor(ageRng, 32, 58);
  const influenceScore = clampLevel(ctx.axes.influence, 22, 92);

  // Physics-to-culture derivation clauses
  const gravityDesc =
    config.gravity > 2.5
      ? "high gravitational pressure demands massive counterweighted architecture, wide basalt terraces, and dense skeletal physiology"
      : config.gravity < 0.6
      ? "low gravitational pull enables towering gossamer suspension spires, effortless three-dimensional flight, and floating stone archways"
      : "balanced gravitational tides support versatile cliffside movement, thermal glider sweeps, and sturdy tension-cable suspension";

  const topologyDesc =
    config.topology === "toroidal"
      ? "the world-loop curves continuously upward so looking zenith-ward reveals the upside-down terraces and hearths of sibling communities"
      : config.topology === "closed"
      ? "closed spherical 3-space bends distant horizons upward, creating the sensation of living inside an immense, glowing bowl"
      : config.topology === "open"
      ? "hyperbolic open geometry causes horizons to flare outward indefinitely into an exhilarating, boundless sky"
      : "flat Euclidean geometry provides pristine, razor-sharp horizons that stretch uninterrupted to the edge of perception";

  const conditionDesc =
    condition === "hostile"
      ? "existence here is conceived as an explicitly speculative thought experiment—a self-organizing pattern of acoustic and electromagnetic harmonics dancing across ionized gas bands"
      : condition === "marginal"
      ? "harsh atmospheric constraints confine civilization to heavily shielded hanging enclaves and deep mantle vaults sustained by geothermal flumes"
      : "favorable cosmological conditions allow open-air hanging terraces to flourish under mild thermal winds and abundant lichen biomes";

  // Reusable relationships
  const rel1 = base.dossier.relationships[0]?.who || "Kaelen";
  const rel2 = base.dossier.relationships[1]?.who || "Mira";
  const rel3 = base.dossier.relationships[2]?.who || "Orin";

  // 1. Identity Chapter
  const ageNote = pick(AGE_COUNTING_NOTES, ageRng);

  const bioVars = {
    name: base.name,
    universeName: universe.name,
    gravityRule: gravityDesc,
    topologyRule: topologyDesc,
    conditionRule: conditionDesc,
    skyCondition: profile.visual.expansion === "accelerating" ? "a fast-fleeing cosmic perimeter" : "a serene starry sea",
    dwellingNature: "the volcanic canyon foundations",
    occupation: base.occupation.toLowerCase(),
    strengthAsset: resolved.strengths[0] ? resolved.strengths[0] : "steadfast curiosity",
    title: base.title,
  };

  const bioP1 = fill(pick(STRANGE_BIOGRAPHY_OPENERS, bioRng), bioVars);
  const bioP2 = fill(pick(STRANGE_BIOGRAPHY_MIDDLES, bioRng), bioVars);
  const bioP3 =
    `Navigating as ${base.occupation}, ${base.name} grounded their daily trade in ${universe.name}'s distinctive physical laws. ` +
    `Whether recalibrating tensioned suspension dampers alongside ${rel1} or charting volatile spatial shears with ${rel2}, ` +
    `they developed a reputation for combining meticulous mathematical precision with profound reverence for their community's collective safety.`;
  const bioP4 = fill(pick(STRANGE_BIOGRAPHY_CLOSERS, bioRng), bioVars);

  const biography = [bioP1, bioP2, bioP3, bioP4];

  // 2. Work Chapter
  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Fledgling Apprentice",
      years: "Cycles 1–6",
      text: `Mastered aerodynamic tension lines and fundamental barometric forecasting along the lower rift margin under senior guidance.`,
    },
    {
      stage: "Independent Rift Navigator",
      years: "Cycles 7–14",
      text: `Piloted independent survey skiffs across fluctuating gravitational folds, establishing reliable transit corridors between isolated hanging spires.`,
    },
    {
      stage: "Master Practitioner & Terrace Trustee",
      years: "Cycles 15–22",
      text: `Supervised municipal suspension integrity, trained fledgling cohorts in shear navigation, and authored authoritative flight logs.`,
    },
    {
      stage: "Chief Regional Cartographer",
      years: "Cycles 23–Present",
      text: `Directs spatial stabilization teams and advises the High Terrace Assembly on long-horizon resource routes across ${universe.name}.`,
    },
  ];

  const responsibilities: string[] = [
    `Surveying fluctuating gravitational shears and barometric tidal gradients across regional transit corridors.`,
    `Calibrating acoustic resonance compasses and tensioned cable dampers to protect settled spires from tectonic tremors.`,
    `Supervising the continuous flow of freshwater condensation flumes supplying the central hanging district.`,
    `Mentoring emerging fledglings in the cognitive and physical arts of three-dimensional aerodynamic navigation.`,
    `Liaising with neighboring terrace arbiters to maintain peaceful water accords and open flight access.`,
    `Maintaining the open-source civic navigation repository so that no pilot flies blind into unmapped fissures.`,
  ];

  const expertiseCandidates = [
    "Non-Euclidean horizon geodesics and spatial fold cartography",
    "Barometric tidal forecasting under heavy atmospheric density",
    "Acoustic resonance seismology and singing basalt calibration",
    "Variable-gravity glide mechanics and aerodynamic counterweights",
    "Extreme-pressure flume engineering and condensation recovery",
    "Emergency high-altitude cable splicing and rigging triage",
  ];
  const expertise = pickDistinct(expertiseCandidates, 5, careerRng);
  const workplace = pick(STRANGE_WORKPLACES, careerRng);

  // 3. Standing Chapter
  const wealthBracket = pick(STRANGE_WEALTH_BRACKETS, standingRng);
  const wealthText =
    `Secured through hereditary shares in municipal transit flumes, lifetime guild dividends, ` +
    `and a personal workshop stocked with irreplaceable brass astrolabes, tension cables, and resonance crystals.`;
  const socialClass = pick(STRANGE_SOCIAL_CLASSES, standingRng);
  const reputation =
    `Legendary across thirty rifts for unflinching orientation during sudden gravitational shears, ` +
    `scrupulous fairness in allocating condensation quotas, and calm moral authority in council.`;
  const prestige =
    `Held in deep reverence by spire governors, merchant pilots, and young fledglings alike as an infallible living repository of physical wisdom.`;
  const influence =
    `Commands direct authority over launch permissions, emergency shelter seals, and civic anchor reinforcements across the hanging archipelago.`;
  const economicCircumstances =
    `Deeply secure against seasonal shortages, insulated by reciprocal trade accords and respected credit in every communal storehouse.`;

  // 4. Home Chapter
  const dwelling = pick(STRANGE_DWELLINGS, homeRng);
  const environment = pick(STRANGE_ENVIRONMENTS, homeRng);
  const lifestyle = pick(STRANGE_LIFESTYLES, homeRng);
  const possessions = pickDistinct(STRANGE_POSSESSIONS_BANK, 5, homeRng);
  const transport = pick(STRANGE_TRANSPORTS, homeRng);

  const routine: string[] = [
    "First tide — Sounding the domestic basalt chime to verify atmospheric density before inspecting outer anchor pins.",
    "Ascending wind — Morning glide sortie along the regional fault line, charting subtle shifts in thermal updrafts.",
    "Zenith alignment — Logging fresh barometric coordinates at the Spire Observatory and testing cable tension with apprentices.",
    "Turn of currents — Midday shared meal of steamed lichen cakes and spiced tea while arbitrating terrace transit quotas.",
    "Descending tide — Hands-on workshop maintenance, polishing quartz apertures and splicing spare carbon-mesh lanyards.",
    "Terminal dusk — Evening communion around the singing stone hearth, decoding seismic hums before retiring to hammock rest.",
  ];

  // 5. People Chapter
  const family = pick(STRANGE_FAMILY_DESCRIPTIONS, peopleRng);
  const relationshipsText =
    `Maintains an enduring, reciprocal partnership with ${rel1}, whose sharp mechanical mind complements their navigational foresight. ` +
    `Shares a lively intellectual rivalry with ${rel2} over mapping newly opened thermal corridors, ` +
    `while guiding the promising apprentice ${rel3} through the rigors of high-altitude flight.`;
  const friendships = pick(STRANGE_FRIENDSHIPS, peopleRng);
  const socialCircle = pick(STRANGE_SOCIAL_CIRCLES, peopleRng);

  // 6. Formative Chapter
  const childhood = pick(STRANGE_CHILDHOODS, formativeRng);
  const education = pick(STRANGE_EDUCATIONS, formativeRng);

  const decisions: string[] = [
    base.choices[0]?.choice || "Choosing the exposed life of a rift navigator over comfortable tenure in the sheltered inner vaults.",
    base.choices[1]?.choice || "Publishing navigational coordinates openly rather than selling exclusive rights to trading cartels.",
    base.choices[2]?.choice || "Adopting bio-resonant sensory training to perceive gravitational shears directly with unaugmented senses.",
  ];

  const achievements: string[] = [
    base.dossier.achievements[0] || "Engineered the multi-tiered counterweight damper system that saved the lower terrace during the Solstice Storm.",
    base.dossier.achievements[1] || "Charted seventy leagues of uncharted thermal wind corridors through the Great Rift, cutting travel times in half.",
    "Discovered that subterranean singing crystals act as natural piezoelectric early-warning sensors for tectonic slip.",
  ];

  const setbacks: string[] = [
    base.dossier.failures[0] || "Misjudged a violent thermal shear during an early test flight, losing an experimental glider to the chasm.",
    base.dossier.failures[1] || "Hesitated during the flood of Cycle 27, arriving too late to prevent an ancestral seed vault from washing into sulfur flats.",
  ];

  const turningPoints: string[] = [
    base.story.turningPoint || "The heroic solo crossing of the collapsed southern rift to bring emergency medicine to stranded miners.",
    "The decisive council confrontation that established open water rights across all twelve hanging settlements.",
  ];

  // 7. Society Chapter
  const societySummary = pick(STRANGE_SOCIETY_SUMMARIES, societyRng);
  const societyLaws = pick(STRANGE_SOCIETY_LAWS, societyRng);
  const societyCulture = pick(STRANGE_SOCIETY_CULTURES, societyRng);
  const societyInstitutions = pick(STRANGE_SOCIETY_INSTITUTIONS, societyRng);
  const societyExpectations = pick(STRANGE_SOCIETY_EXPECTATIONS, societyRng);
  const societyOpportunity = pick(STRANGE_SOCIETY_OPPORTUNITIES, societyRng);

  // 8. World Chapter
  const worldTech = pick(STRANGE_WORLD_TECHNOLOGIES, worldRng);
  const worldScience = pick(STRANGE_WORLD_SCIENCES, worldRng);
  const worldGeography = pick(STRANGE_WORLD_GEOGRAPHIES, worldRng);
  const worldHistory = pick(STRANGE_WORLD_HISTORIES, worldRng);
  const worldEnvironment = pick(STRANGE_WORLD_ENVIRONMENTS, worldRng);

  // 9. How It Differs Chapter
  const howDiffersHeadline =
    "A civilization woven directly into anomalous physics, where survival turns strange cosmological laws into everyday grace.";

  const howDiffersParagraphs = [
    `Unlike Earth's uniform, three-dimensional Euclidean reality, this universe features ${topologyDesc} and ${gravityDesc}. Daily life cannot rely on flat pavement or predictable gravity; instead, urban architecture hangs gracefully in tension across three-dimensional canyons, turning navigation into an art of curved aerodynamics.`,
    `Economic survival is grounded not in paper finance or digital speculation, but in the physical stewardship of shared condensation flumes, resonant anchor cables, and thermal updraft passes. Institutions exist strictly because of the physics: guilds arose to monitor gravitational tides, laws mandate the immediate sharing of navigation charts, and property is measured in structural anchor shares.`,
    `Human sensory perception has adapted to register subtle barometric shears, electromagnetic halos, and acoustic ground frequencies. Where an Earth observer would perceive overwhelming vertigo and impossible geometry, the inhabitants of this cosmos experience an affectionate, harmonious home rich in communal warmth, breathtaking vistas, and daily purpose.`,
  ];

  const keyDifferences = [
    {
      title: "Non-Euclidean Spatial Geography",
      text: "Spatial curvature and topological loops compress planetary distances, making navigation a matter of calculating geometric shortcuts rather than straight lines.",
    },
    {
      title: "Harmonic Tidal Gravity",
      text: "Variable gravitational currents allow massive basalt islands and hanging dwellings to float effortlessly on rising thermal and gravitational updrafts.",
    },
    {
      title: "Physics-Driven Civic Institutions",
      text: "Laws, guilds, and social customs exist to manage the environment: mandatory mutual rescue at sea, open-source cartography, and communal flume stewardship.",
    },
    {
      title: "Resonant Material Craft",
      text: "Technology favors acoustic resonance, flexible carbon-mesh cables, and piezoelectric crystals over fossil-fueled combustion machinery.",
    },
  ];

  // 10. Day in the Life Chapter
  const beats: LifeBeat[] = [
    {
      time: "First tide",
      heading: "Harmonic Dawn at the Eyrie",
      text:
        `Awakens inside the suspended dome as the twin suns illuminate the canyon mist in shades of pale amber and copper. ` +
        `Checks the tension indicators on the main basalt anchor cables and drinks a warm bowl of steeped alpine lichen tea. ` +
        `${rel1} calls up from the lower terrace to confirm that the morning's barometric tide is stable for open-sky transit.`,
    },
    {
      time: "Rising wind",
      heading: "Skiff Rigging and Apprentice Check",
      text:
        `Steps out onto the launch platform to inspect the resonance skiff's mica sails alongside the eager apprentice ${rel3}. ` +
        `Notices a small hairline wear mark on a secondary steering cable and demonstrates how to splice it bare-handed with carbon thread. ` +
        `The lesson instills patience in the fledgling while ensuring total flight safety before crossing the open abyss.`,
    },
    {
      time: "Second tide",
      heading: "Traversing the Chasm Fault",
      text:
        `Launches the skiff into the rising thermal currents, guiding the craft through a volatile gravitational shear where the cliff walls narrow. ` +
        `Calculates the non-Euclidean curve of the canyon gap with effortless intuitive poise, feeling the air density thicken against the hull. ` +
        `The crossing is breathtaking: floating stone pillars drift silently on ascending breezes thousands of meters below.`,
    },
    {
      time: "Zenith turn",
      heading: "Observation at the High Spire",
      text:
        `Docks at the Spire Observatory to deliver newly logged flight coordinates and meet with the senior archivist. ` +
        `A minor disagreement arises with ${rel2} over whether the southern rift corridor is expanding faster than seasonal models predict. ` +
        `They review the raw barometric telemetry together, agreeing to mount a joint calibration survey at tomorrow's ascending tide.`,
    },
    {
      time: "Descending wind",
      heading: "Condensation Flume Inspection",
      text:
        `Descends to the mid-level terraces to inspect the district's primary freshwater flumes with municipal engineers. ` +
        `Tightens an overheating pressure-relief governor along the geothermal conduit, preventing a disruptive steam venting into the residential tier. ` +
        `Grateful terrace residents offer dried sweet moss and fresh water gourds in heartfelt appreciation for the prompt repair.`,
    },
    {
      time: "Low tide",
      heading: "Council Debate at the Hearth",
      text:
        `Attends the terrace assembly where delegates debate whether to expand new hanging gardens into the lower thermal fissure. ` +
        `Speaks with calm authority, pointing out that seismic vibrations in the basalt foundation require additional counterweight dampers before construction begins. ` +
        `The assembly unanimously approves the prudent amendment, affirming their trust in seasoned navigational counsel.`,
    },
    {
      time: "Terminal dusk",
      heading: "Glider Return Across Violet Skies",
      text:
        `Steers the skiff on the gentle homebound glide as the amber suns dip behind the jagged basalt towers, casting long prismatic shadows. ` +
        `Passes neighboring scouts who signal greetings with flashing quartz lanterns, their light bouncing across the vast void. ` +
        `Glides into the home mooring cradle with quiet precision, securing the docking halyards as the wind drops to a peaceful whisper.`,
    },
    {
      time: "Night calm",
      heading: "Acoustic Vigil and Rest",
      text:
        `Shares an evening meal of seasoned mushroom broth with ${rel1}, laughing over the day's close calls and quiet triumphs. ` +
        `Transcribes the day's flight logs onto flexible quartz sheets, listening to the deep, comforting drone of the planetary steam vents. ` +
        `Retires to the suspended sleeping hammock as bioluminescent lichens cast a serene emerald glow over the peaceful canyon.`,
    },
  ];

  const closingDayText =
    `As the twin suns fold behind the distant curved horizon and the rift canyon glows with soft bioluminescent light, ` +
    `peace reigns across the hanging spires, guarded by chosen vigilance and the enduring strength of the bedrock.`;

  // 11. Futures Chapter
  const futuresPaths: LifeFuturePath[] = [
    {
      id: "a",
      name: "Grand Custodian of the Common Sky",
      flavor: "Most natural",
      summary:
        `Gradual elevation to lead the entire regional federation of rift cartographers, ` +
        `formalizing navigation safety standards across all thirty hanging terrace settlements.`,
      opportunities: [
        "Instituting universal open-access flight training for every emerging generation",
        "Authoring the definitive multi-volume atlas of non-Euclidean rift geometries",
        "Securing generational peace accords regarding shared condensation flumes",
      ],
      challenges: [
        "Managing growing administrative friction among autonomous spire councils",
        "Relinquishing daily hands-on solo flight time to handle council governance",
        "Balancing traditional conservation with pressure to expand into volatile zones",
      ],
      consequences:
        `Enduring civic veneration as a legendary guardian whose prudent foresight protected thousands of lives above the abyss.`,
    },
    {
      id: "b",
      name: "Architect of the Resonance Bridge",
      flavor: "Ambitious",
      summary:
        `Spearheading the engineering of permanent harmonic resonance bridges that span the Great Rift, ` +
        `physically uniting estranged canyon communities into an unbroken economic commonwealth.`,
      opportunities: [
        "Ending centuries of dangerous solo glider crossings for ordinary trade caravans",
        "Tripling inter-terrace commerce and stabilizing regional food reserves",
        "Pioneering revolutionary high-tensile carbon cable alloys tested under extreme pressure",
      ],
      challenges: [
        "Overcoming vehement opposition from conservative fold pilots fearing obsolescence",
        "Enormous resource demands requiring unproven geothermal power tap reactors",
        "Unprecedented structural strain during violent seasonal barometric shears",
      ],
      consequences:
        `A transformed planetary civilization with permanent aerial highways linking once-isolated spires across former voids.`,
    },
    {
      id: "c",
      name: "Voyager of the Uncharted Rift Chasm",
      flavor: "Wildcard",
      summary:
        `Relinquishing civic titles to mount a daring, deep-chasm expedition into the forbidden lower geothermal abyss, ` +
        `seeking the primordial physical source of the world's fluctuating constants.`,
      opportunities: [
        "Discovering pristine biomes and untouched mineral crystals unseen for centuries",
        "Unlocking deep cosmological secrets that could explain the origin of local gravity tides",
        "Total operational freedom beyond the reach of municipal regulations and trade guilds",
      ],
      challenges: [
        "Extreme barometric pressures capable of crushing unshielded flight hulls",
        "Complete severing of contact with familiar hanging settlements and loved ones",
        "Navigating uncharted magnetic squalls without existing cartographic references",
      ],
      consequences:
        `A mythic journey recorded in song and lore across every hanging terrace, remembered as the boldest leap into the unknown.`,
    },
  ];

  // 12. Timeline Chapter (9 entries, strictly ascending ages)
  const tAge1 = 6;
  const tAge2 = 14;
  const tAge3 = 19;
  const tAge4 = 25;
  const tAge5 = Math.max(27, age - 8);
  const tAge6 = Math.max(tAge5 + 2, age - 3);
  const tAge7 = age;
  const tAge8 = age + 6;
  const tAge9 = age + 14;

  const timeline: LifeTimelineEntry[] = [
    {
      age: tAge1,
      label: `Age ${tAge1}`,
      title: "Awakening on the Terraces",
      text: `Grew up along the undulating basalt cliffs of ${universe.name}, learning to judge gravitational tides before walking unsupported.`,
      kind: "origin",
    },
    {
      age: tAge2,
      label: `Age ${tAge2}`,
      title: "First Solo Glider Leap",
      text: `Successfully piloted a training skiff across the narrow northern canyon gorge during a minor barometric shear.`,
      kind: "education",
    },
    {
      age: tAge3,
      label: `Age ${tAge3}`,
      title: "The Navigational Vow",
      text: decisions[0] || `Dedicated life to mapping volatile rift corridors rather than remaining in sheltered inner vaults.`,
      kind: "choice",
    },
    {
      age: tAge4,
      label: `Age ${tAge4}`,
      title: "Discovery of the Silver Pass",
      text: achievements[0] || `Mapped a dependable thermal updraft corridor through formerly impassable gravitational turbulence.`,
      kind: "achievement",
    },
    {
      age: tAge5,
      label: `Age ${tAge5}`,
      title: "The Great Rift Shear",
      text: setbacks[0] || `Survived twelve cycles isolated on a remote spire after a sudden pressure inversion destroyed their primary skiff.`,
      kind: "setback",
    },
    {
      age: tAge6,
      label: `Age ${tAge6}`,
      title: "Strategic Resolution",
      text: turningPoints[0] || `Led the emergency restoration of municipal water flumes following a severe seismic tremor.`,
      kind: "turning",
    },
    {
      age: tAge7,
      label: `Age ${tAge7}`,
      title: "Master Cartographer & Trustee",
      text: `Commands the regional spatial charting guild, safeguarding transit lines and training the next generation of fledglings.`,
      kind: "present",
    },
    {
      age: tAge8,
      label: `Age ${tAge8}`,
      title: "The Great Chasm Survey",
      text: `Prepares to lead an exploratory expedition into the outer perimeter canyons to map emerging geothermal vents.`,
      kind: "future",
    },
    {
      age: tAge9,
      label: `Age ${tAge9}`,
      title: "Generational Legacy",
      text: `Anticipates transitioning to the Elder Council, enshrining decades of field logs into the permanent Spire Archives.`,
      kind: "future",
    },
  ];

  // 13. Earth Comparison Chapter
  const divergences: EarthDivergence[] = [
    {
      id: "d1",
      title: "Non-Euclidean Topology and Variable Gravitation",
      text: `This universe operates under ${topologyDesc} and ${gravityDesc}, whereas Earth features rigid, uniform 1-G Euclidean space.`,
      effects: `Architecture hangs in three-dimensional tension, transportation relies on aerodynamic gliders and counterweights, and distance is elastic.`,
    },
    {
      id: "d2",
      title: "Atmospheric Density and Geothermal Flume Infrastructure",
      text: `Atmospheric density and geothermal vents support aerial suspension and communal condensation flumes rather than surface-bound combustion grids.`,
      effects: `Industry and civic life are organized around shared water preservation, acoustic signaling, and zero-emission fluid mechanics.`,
    },
    {
      id: "d3",
      title: "Physics-Derived Cooperative Guild Governance",
      text: `Because environmental extremes make individual survival impossible, societal laws mandate mutual rescue, open cartography, and public resource stewardship.`,
      effects: `Replaces financial speculation and adversarial politics with democratic guild oversight and consensus-driven public safety boards.`,
    },
    {
      id: "d4",
      title: "Perceptual and Sensory Sensory Adaptation",
      text: `Conscious awareness adapted to perceive four-dimensional shears, atmospheric ionization, and acoustic ground harmonics directly.`,
      effects: `Culture, art, and philosophy treat multi-dimensional physical laws with serene intimacy and aesthetic reverence rather than terror.`,
    },
  ];

  const earthRows: EarthRow[] = [
    {
      dimension: "Occupation",
      here: base.occupation,
      earth: "Logistics coordinator, commercial pilot, or civil engineer",
      because: `Divergence d1 requires specialized topological navigation and counterweight calibration unknown to Earth's flat surface transit.`,
      divergenceId: "d1",
    },
    {
      dimension: "Social status",
      here: `${socialClass} — held in deep civic reverence`,
      earth: "Mid-level corporate or municipal technical specialist",
      because: `Divergence d3 anchors social standing in direct infrastructural protection and life-saving cartography rather than monetary wealth.`,
      divergenceId: "d3",
    },
    {
      dimension: "Financial circumstances",
      here: `${wealthBracket} — guaranteed flume dividends and workshop equity`,
      earth: "Wage income vulnerable to market inflation and rent burdens",
      because: `Divergence d2 replaces speculative financialization with permanent communal equity in water flumes and transit charters.`,
      divergenceId: "d2",
    },
    {
      dimension: "Relationships",
      here: `Intensely bonded kinship circles forged through shared high-altitude vigil`,
      earth: "Geographically diffuse and digital-mediated social networks",
      because: `Divergence d3 fosters unbreakable interpersonal loyalty when neighbors literally hold one another's life tethers.`,
      divergenceId: "d3",
    },
    {
      dimension: "Freedoms",
      here: `Total three-dimensional mobility across open atmospheric corridors`,
      earth: "Restricted by rigid surface borders, property fences, and workplace surveillance",
      because: `Divergence d1 allows agile travelers on glide-wings to traverse vast canyon spans without physical checkpoints.`,
      divergenceId: "d1",
    },
    {
      dimension: "Daily life",
      here: `Dynamic, sensory-rich physical communion with wind, stone, and resonance`,
      earth: "Screen-mediated indoor routines dominated by repetitive desk tasks",
      because: `Divergence d4 attunes daily life to acoustic chimes, barometric tides, and open-air glider flights.`,
      divergenceId: "d4",
    },
    {
      dimension: "Available technology",
      here: `Acoustic resonance generators, quartz prism optics, and carbon tension rigs`,
      earth: "Silicon electronics, fossil-fueled engines, and planned-obsolescence hardware",
      because: `Divergence d2 stimulated fluid and acoustic mechanics that operate with centurial durability under extreme pressures.`,
      divergenceId: "d2",
    },
    {
      dimension: "Future opportunities",
      here: `Charting untouched dimensional rifts and shaping planetary bridge networks`,
      earth: "Incremental career advancement within crowded, saturated industries",
      because: `Divergence d1 keeps the world's non-Euclidean frontiers vast, unmapped, and full of genuine pioneering promise.`,
      divergenceId: "d1",
    },
  ];

  // Visitor signals linking resolved answers
  const visitorSignals = [
    `Your declared appetite for ${listJoin(resolved.interests.map((i) => i.toString()))} shaped this persona's trade as ${base.occupation.toLowerCase()}.`,
    `Your chosen strength of ${listJoin(resolved.strengths.map((s) => s.toString()))} anchored their cool-headed poise during severe barometric emergencies.`,
    resolved.filled.ambition
      ? `Because ambition was left open, the generator assigned a drive toward ${resolved.ambition}, manifesting in their devotion to open-source navigation.`
      : `Your declared ambition toward ${resolved.ambition} directly framed their defining tension between exploratory longing and civic duty.`,
    resolved.filled.lifePath
      ? `Because life path was omitted, the generator grounded them along the ${resolved.lifePath} walk, centering them as a key community trustee.`
      : `Your selected life path as ${resolved.lifePath} positioned them at the core of the hanging district's suspension safety.`,
  ];

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: base.name,
      age,
      ageNote,
      personalitySummary: base.dossier.personality,
      biography,
    },
    work: {
      occupation: base.occupation,
      expertise,
      careerHistory,
      responsibilities,
      workplace,
    },
    standing: {
      wealthBracket,
      wealthText,
      socialClass,
      reputation,
      prestige,
      influence,
      influenceLevel: influenceScore,
      economicCircumstances,
    },
    home: {
      dwelling,
      environment,
      lifestyle,
      possessions,
      transport,
      routine,
    },
    people: {
      family,
      relationships: relationshipsText,
      friendships,
      socialCircle,
    },
    formative: {
      childhood,
      education,
      decisions,
      achievements,
      setbacks,
      turningPoints,
    },
    society: {
      summary: societySummary,
      laws: societyLaws,
      culture: societyCulture,
      institutions: societyInstitutions,
      expectations: societyExpectations,
      opportunity: societyOpportunity,
    },
    world: {
      technology: worldTech,
      science: worldScience,
      geography: worldGeography,
      history: worldHistory,
      environment: worldEnvironment,
    },
    howDiffers: {
      headline: howDiffersHeadline,
      paragraphs: howDiffersParagraphs,
      keyDifferences,
    },
    dayInLife: {
      title: "A Day in This Life",
      beats,
      closing: closingDayText,
    },
    futures: {
      title: "Three Possible Futures",
      paths: futuresPaths,
      disclaimer: "These pathways are imaginative story branches derived from cosmological model parameters, not empirical predictions.",
    },
    timeline,
    earthComparison: {
      basis: "general-reference",
      basisNote: "The Earth column serves as a general reference for typical modern terrestrial circumstances, as no personal data is collected.",
      rows: earthRows,
      divergences,
      visitorSignals,
    },
  };
}
